import React, { useState, useEffect, useRef } from "react";
import {
  fetchPaymentPurposes,
  createPaymentPurpose,
  updatePaymentPurpose,
  deletePaymentPurpose,
} from "../../api/financePaymentMastersApi";
import { getCsrfToken } from "../../../../public/api/authApi";
import {
  GlobalButton,
  GlobalTextField,
  GlobalSelectField,
} from "../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../global/GlobalModal";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../global/GlobalBadge";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import GlobalSuccessFlashModal from "../../../../global/GlobalSuccessFlashModal";

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Inactive" },
];

const PaymentPurposesMaster = ({ onDirectoryChange }) => {
  const [purposes, setPurposes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    purpose_name: "",
    description: "",
    status_id: 1,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);

  const scrollToMasterForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  };

  useEffect(() => {
    loadPurposes();
  }, []);

  const loadPurposes = async () => {
    try {
      setLoading(true);
      const response = await fetchPaymentPurposes();
      if (response && response.success) {
        setPurposes(response.data || []);
      } else {
        setPurposes(response || []);
      }
    } catch (err) {
      setError(err.message || "Failed to load payment purposes");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrorDetails[name]) {
      setFormErrorDetails((prev) => ({ ...prev, [name]: "" }));
    }
    setFormError(null);
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFormError(null);
    setFormErrorDetails({});
    setSuccessMsg("");
    const wasEdit = isEditing;
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      if (wasEdit) {
        await updatePaymentPurpose(
          formData.id,
          {
            purpose_name: formData.purpose_name.trim(),
            description: formData.description,
            status_id: Number(formData.status_id),
          },
          csrfToken,
        );
      } else {
        await createPaymentPurpose(
          {
            purpose_name: formData.purpose_name.trim(),
            description: formData.description,
          },
          csrfToken,
        );
      }
      setFormData({ id: null, purpose_name: "", description: "", status_id: 1 });
      setIsEditing(false);
      setSuccessMsg(
        wasEdit
          ? "Payment purpose updated successfully."
          : "Payment purpose added successfully.",
      );
      await loadPurposes();
      onDirectoryChange?.();
    } catch (err) {
      setFormError(err.message);
    }
  };

  const handleEdit = (purpose) => {
    setSuccessMsg("");
    setFormData({
      id: purpose.id,
      purpose_name: purpose.purpose_name,
      description: purpose.description || "",
      status_id:
        purpose.status_id != null && purpose.status_id !== ""
          ? Number(purpose.status_id)
          : 1,
    });
    setIsEditing(true);
    scrollToMasterForm();
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deletePaymentPurpose(itemToDelete.id, csrfToken);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setSuccessMsg("Payment purpose removed from the directory.");
      await loadPurposes();
      onDirectoryChange?.();
    } catch (err) {
      alert("Failed to remove: " + err.message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ id: null, purpose_name: "", description: "", status_id: 1 });
    setFormError(null);
    setFormErrorDetails({});
    setIsEditing(false);
  };

  if (loading) return <p className="ub-loading">Loading payment purposes…</p>;
  if (error) return <p className="ub-error">Error: {error}</p>;

  const dataList = Array.isArray(purposes) ? purposes : purposes.data || [];
  const ALERT_ID = "fp-purpose-master-alert";

  return (
    <div className="ub-master-section">
      <GlobalSuccessFlashModal
        message={successMsg}
        onDismiss={() => setSuccessMsg("")}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.25rem",
        }}
      >
        <h3 className="ub-section__subhead" style={{ margin: 0 }}>
          Payment purpose master
        </h3>
        {/* <GlobalButton
          variant="primary"
          size="sm"
          onClick={() => {
            setSuccessMsg("");
            setFormData({
              id: null,
              purpose_name: "",
              description: "",
              status_id: 1,
            });
            setIsEditing(false);
            setFormError(null);
            setFormErrorDetails({});
            scrollToMasterForm();
          }}
        >
          Add new purpose
        </GlobalButton> */}
      </div>

      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        <GlobalFormShell
          legend={isEditing ? "Edit payment purpose" : "Add payment purpose"}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton variant="ghost" onClick={handleCancel}>
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton variant="primary" onClick={handleSubmit}>
                {isEditing ? "Save changes" : "Create purpose"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id={isEditing ? "purpose_name_edit" : "purpose_name"}
            label="Purpose name"
            value={formData.purpose_name}
            onChange={(v) => handleInputChange("purpose_name", v)}
            error={formErrorDetails.purpose_name}
            baseAlertId={ALERT_ID}
            required
            placeholder="e.g. Membership fee, Tournament fee, Donation"
          />
          <GlobalTextField
            id={isEditing ? "purpose_description_edit" : "purpose_description"}
            label="Description"
            value={formData.description}
            onChange={(v) => handleInputChange("description", v)}
            error={formErrorDetails.description}
            baseAlertId={ALERT_ID}
            placeholder="Optional details for reports and filters"
          />
          {isEditing ? (
            <GlobalSelectField
              id="purpose_status_id"
              label="Status"
              value={String(formData.status_id)}
              onChange={(v) => handleInputChange("status_id", Number(v))}
              options={STATUS_OPTIONS}
              baseAlertId={ALERT_ID}
              placeholderOption="Select active or inactive…"
            />
          ) : null}
        </GlobalFormShell>
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel="Payment purposes master table"
        headers={[
          { key: "purpose_name", label: "Purpose name" },
          { key: "description", label: "Description" },
          { key: "status", label: "Status" },
          { key: "created_at", label: "Created date" },
          {
            key: "actions",
            label: "Actions",
            className: "ub-table__col-actions",
          },
        ]}
      >
        {dataList.map((purpose) => (
          <tr key={purpose.id}>
            <td style={{ fontWeight: "500" }}>{purpose.purpose_name}</td>
            <td>{purpose.description || "—"}</td>
            <td>
              <GlobalBadge active={Number(purpose.status_id) === 1} />
            </td>
            <td>
              {purpose.created_at
                ? new Date(purpose.created_at).toLocaleDateString()
                : "—"}
            </td>
            <td className="ub-table__col-actions">
              <div className="ub-table-actions">
                <GlobalTableActionButton action="edit" onClick={() => handleEdit(purpose)} />
                <GlobalTableActionButton
                  action="delete"
                  onClick={() => {
                    setItemToDelete(purpose);
                    setShowDeleteConfirm(true);
                  }}
                />
              </div>
            </td>
          </tr>
        ))}
        {dataList.length === 0 && (
          <tr>
            <td
              colSpan={5}
              style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}
            >
              No payment purposes found
            </td>
          </tr>
        )}
      </GlobalDataTable>

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title="Confirm removal"
        footer={
          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              justifyContent: "flex-end",
            }}
          >
            <GlobalButton
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </GlobalButton>
            <GlobalButton
              variant="danger"
              onClick={handleConfirmDelete}
              loading={isDeleting}
            >
              Remove
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove payment purpose <b>{itemToDelete?.purpose_name}</b> from the
          directory?
        </p>
      </GlobalModal>
    </div>
  );
};

export default PaymentPurposesMaster;
